import type {ReactElement} from 'react';
import React from 'react';
import {ContextualMenuDropdownOption} from '@Components/contextual-menu/components/contextual-menu-dropdown-option';
import type {ColorFillStorage} from '@Components/contextual-menu/components/contextual-menu-color-fill-option';
import {ContextualMenuColorFillOption} from '@Components/contextual-menu/components/contextual-menu-color-fill-option';
import type {RGBA} from '@Utils/color.util';
import {getColorObjectArrayFromFillColor} from '@Utils/color.util';
import {ContextualMenuSliderOptions} from '@Components/contextual-menu/components/contextual-menu-slider-options';
import {LayoutBackgroundTypes} from '@PosterWhiteboard/items/layouts/layout.types';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromTabItem} from '@Hooks/poster-editor/useGetKeyFromTabItem';

interface TabsBackgroundOptionProps {
  onClickColorFill(): void;
}

export function TabsBackgroundOption({...props}: TabsBackgroundOptionProps): ReactElement {
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const itemBgColor = useGetKeyFromTabItem('itemBackgroundColor');
  const itemBgColorType = useGetKeyFromTabItem('backgroundType') as LayoutBackgroundTypes;

  if (!activeItem) {
    throw new Error('Active Item is null');
  }

  if (!activeItem.isTab() || itemBgColor === undefined || itemBgColorType === undefined) {
    throw new Error(`Expected tab item, instead got ${activeItem.gitype}`);
  }

  const onClickHandler = (clickedId: string): void => {
    void activeItem.updateFromObject({
      backgroundType: Number(clickedId),
    });
  };

  const onClickColorFill = (): void => {
    props.onClickColorFill();
  };

  const getDataForColorObject = (): ColorFillStorage[] => {
    return [
      {
        id: 'tab-background-color-object',
        color: getColorObjectArrayFromFillColor([itemBgColor]),
      },
    ];
  };

  const getColorSubOption = (): ReactElement => {
    return (
      <ContextualMenuColorFillOption id="tab-background-color-fill-option" label={window.i18next.t('pmwjs_color')} data={getDataForColorObject()} onClick={onClickColorFill} />
    );
  };

  const onUpdateAlpha = (backgroundColorAlpha: number, undoable = true): void => {
    const backgroundColor = itemBgColor.slice() as RGBA;
    backgroundColor[3] = backgroundColorAlpha;
    void activeItem.updateFromObject(
      {
        itemBackgroundColor: backgroundColor,
      },
      {
        undoable,
      }
    );
  };

  const onChangeAlpha = (updatedValue: number): void => {
    onUpdateAlpha(updatedValue, false);
  };
  const onChangeAlphaEnded = (updatedValue: number): void => {
    onUpdateAlpha(updatedValue);
  };

  const getOpacitySubOption = (): ReactElement => {
    return (
      <ContextualMenuSliderOptions
        id="tab-background-color-opacity"
        value={itemBgColor[3]}
        label={window.i18next.t('pmwjs_opacity')}
        range={{
          min: 0,
          max: 1,
        }}
        transformDisplay={{
          min: 0,
          max: 100,
        }}
        onChange={onChangeAlpha}
        onChangeEnded={onChangeAlphaEnded}
      />
    );
  };

  const getSubOptions = (): ReactElement | null => {
    if (itemBgColorType === LayoutBackgroundTypes.NONE) {
      return null;
    }

    return (
      <>
        {getColorSubOption()}
        {getOpacitySubOption()}
      </>
    );
  };

  return (
    <ContextualMenuDropdownOption
      id="tab-item-background-option"
      label={window.i18next.t('pmwjs_background')}
      onClick={onClickHandler}
      dropdownItems={[
        {id: LayoutBackgroundTypes.NONE, text: window.i18next.t('pmwjs_transparent')},
        {id: LayoutBackgroundTypes.COLOR, text: window.i18next.t('pmwjs_color')},
      ]}
      selectedItemId={itemBgColorType}
    >
      {getSubOptions()}
    </ContextualMenuDropdownOption>
  );
}
